/**
 * Brand mark + wordmark used in the navbar and on auth pages.
 *
 * The SVG mark uses `currentColor` so the calling context drives the
 * color — brand teal in the navbar, white on a dark login background,
 * etc. The wordmark is HTML text so it inherits the surrounding font
 * stack (no need to embed a webfont in the SVG).
 */
import type { CSSProperties } from "react";

type Size = "sm" | "md" | "lg";

// Mark size matches the wordmark's line-height box so the two flex items
// have the same height and the SVG's vertical center sits exactly on the
// text's optical center. Anything taller and the mark dominates the brand
// container's height, pushing the brand visually out of line with the
// adjacent <Nav.Link>s (which use Bootstrap's default 1.5 line-height).
const SIZE_PX: Record<Size, { mark: number; text: string }> = {
  sm: { mark: 18, text: "0.95rem" },
  md: { mark: 22, text: "1.15rem" },
  lg: { mark: 32, text: "1.6rem" },
};

export function BrandLogo({
  size = "md",
  showWordmark = true,
  color = "var(--naas-brand)",
}: {
  size?: Size;
  showWordmark?: boolean;
  color?: string;
}) {
  const { mark, text } = SIZE_PX[size];
  // line-height inherits from the surrounding Bootstrap context (1.5).
  // Keeping it inherited makes the wordmark's baseline sit on the same
  // baseline as the navbar's <Nav.Link> text — explicitly setting a
  // tighter line-height (e.g. 1) caused the brand to ride visually high
  // against the adjacent nav items.
  const wordmarkStyle: CSSProperties = {
    fontWeight: 600,
    fontSize: text,
    letterSpacing: "-0.01em",
  };

  return (
    <span
      className="d-inline-flex align-items-center gap-2"
      style={{ color }}
    >
      <Mark size={mark} />
      {showWordmark && <span style={wordmarkStyle}>NaaS Manager</span>}
    </span>
  );
}

function Mark({ size }: { size: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      role="img"
      aria-label="NaaS Manager logo"
    >
      <line
        x1="9.5"
        y1="16"
        x2="22.5"
        y2="16"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <circle cx="9" cy="16" r="4.5" fill="currentColor" />
      <circle cx="23" cy="16" r="4.5" fill="currentColor" />
    </svg>
  );
}
